import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  RefreshCw, Users, UserCheck, UserX, TrendingUp,
  ChevronDown, ChevronUp, Save, Mail, MessageSquare,
  Clock, ArrowRight, Play, Pause, Trash2, Activity,
  Settings, BarChart3, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
  { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
  { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
];

const STATUS_MAP = {
  active: { label: 'Aktiv', color: 'bg-green-100 text-green-700 border-green-300' },
  converted: { label: 'Konvertiert', color: 'bg-blue-500/20 text-blue-600 border-blue-500/30' },
  unsubscribed: { label: 'Abgemeldet', color: 'bg-orange-500/20 text-orange-600 border-orange-500/30' },
  completed: { label: 'Abgeschlossen', color: 'bg-gray-100 text-gray-600 border-gray-300' },
  removed: { label: 'Entfernt', color: 'bg-red-100 text-red-700 border-red-300' },
};

export const WinBackFlow = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [tab, setTab] = useState('flow');
  const [saving, setSaving] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [savingTemplates, setSavingTemplates] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }), []);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgRes, statsRes, custRes, actRes, tplRes] = await Promise.all([
        axios.get(`${API}/admin/winback/config`, { headers: headers() }),
        axios.get(`${API}/admin/winback/stats`, { headers: headers() }),
        axios.get(`${API}/admin/winback/customers?limit=50`, { headers: headers() }),
        axios.get(`${API}/admin/winback/activity?limit=30`, { headers: headers() }),
        axios.get(`${API}/admin/winback/templates`, { headers: headers() }),
      ]);
      setConfig(cfgRes.data);
      setEditConfig(cfgRes.data);
      setStats(statsRes.data);
      setCustomers(custRes.data || []);
      setActivity(actRes.data || []);
      setTemplates(tplRes.data);
    } catch (err) {
      console.error('Win-back fetch error:', err);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggleActive = async () => {
    try {
      const newState = !editConfig.is_active;
      await axios.put(`${API}/admin/winback/config`, {
        ...editConfig,
        is_active: newState,
      }, { headers: headers() });
      setEditConfig(prev => ({ ...prev, is_active: newState }));
      setConfig(prev => ({ ...prev, is_active: newState }));
      toast.success(newState ? 'Win-Back Flow aktiviert' : 'Win-Back Flow deaktiviert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/winback/config`, editConfig, { headers: headers() });
      setConfig(editConfig);
      toast.success('Konfiguration gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const updateStage = (idx, field, value) => {
    setEditConfig(prev => {
      const stages = [...prev.stages];
      stages[idx] = { ...stages[idx], [field]: value };
      return { ...prev, stages };
    });
  };

  const handleRemoveCustomer = async (trackerId) => {
    try {
      await axios.delete(`${API}/admin/winback/customers/${trackerId}`, { headers: headers() });
      toast.success('Kunde aus Flow entfernt');
      fetchAll();
    } catch (err) {
      toast.error('Fehler beim Entfernen');
    }
  };

  const handleSaveTemplates = async () => {
    setSavingTemplates(true);
    try {
      await axios.put(`${API}/admin/winback/templates`, templates, { headers: headers() });
      toast.success('Vorlagen gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingTemplates(false);
    }
  };

  const updateTemplate = (key, value) => {
    setTemplates(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (!config) return null;

  return (
    <Card data-testid="winback-flow-section">
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-amber-500" />
            <div>
              <CardTitle className="text-lg">Win-Back Automation</CardTitle>
              <CardDescription>
                Automatische Rückgewinnung von Neukunden ohne Buchung
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge
              data-testid="winback-status-badge"
              className={config.is_active
                ? 'bg-green-100 text-green-700 border-green-300'
                : 'bg-gray-100 text-gray-600 border-gray-300'}
            >
              {config.is_active ? 'Aktiv' : 'Inaktiv'}
            </Badge>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-6" data-testid="winback-flow-content">
          {/* Tab Navigation */}
          <div className="flex gap-2 border-b pb-2" data-testid="winback-tabs">
            {[
              { id: 'flow', label: 'Flow', icon: Eye },
              { id: 'settings', label: 'Einstellungen', icon: Settings },
              { id: 'vorlagen', label: 'Vorlagen', icon: Mail },
              { id: 'customers', label: 'Kunden', icon: Users },
              { id: 'activity', label: 'Log', icon: Activity },
            ].map(t => (
              <button
                key={t.id}
                data-testid={`winback-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  tab === t.id
                    ? 'bg-amber-100 text-amber-700'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          {/* Stats KPIs */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="winback-stats">
              <StatCard label="Gesamt" value={stats.total} icon={Users} />
              <StatCard label="Aktiv" value={stats.active} icon={Play} color="text-green-600" />
              <StatCard label="Konvertiert" value={stats.converted} icon={UserCheck} color="text-blue-600" />
              <StatCard label="Abgemeldet" value={stats.unsubscribed} icon={UserX} color="text-orange-600" />
              <StatCard label="Konversionsrate" value={`${stats.conversion_rate}%`} icon={TrendingUp} color="text-amber-600" />
            </div>
          )}

          {/* Discount-Code KPIs */}
          {stats?.codes && (
            <div
              className="grid grid-cols-3 gap-3"
              data-testid="winback-code-stats"
            >
              <StatCard
                label="Codes ausgegeben"
                value={stats.codes.issued}
                icon={Mail}
                color="text-purple-400"
              />
              <StatCard
                label="Codes eingelöst"
                value={stats.codes.used}
                icon={UserCheck}
                color="text-emerald-600"
              />
              <StatCard
                label="Einlöse-Rate"
                value={`${stats.codes.redemption_rate}%`}
                icon={TrendingUp}
                color="text-amber-600"
              />
            </div>
          )}

          {/* Flow Tab */}
          {tab === 'flow' && <FlowVisualization config={editConfig} stats={stats} />}

          {/* Settings Tab */}
          {tab === 'settings' && (
            <div className="space-y-4" data-testid="winback-settings-tab">
              <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
                <div>
                  <Label className="text-base font-semibold">Flow aktivieren</Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Automatisch neue Kunden ohne Buchung kontaktieren
                  </p>
                </div>
                <Switch
                  data-testid="winback-active-toggle"
                  checked={editConfig.is_active}
                  onCheckedChange={handleToggleActive}
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">Stufen-Konfiguration</h3>
                {editConfig.stages?.map((stage, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${STAGE_COLORS[idx]?.border || 'border-gray-200'} ${STAGE_COLORS[idx]?.bg || 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${STAGE_COLORS[idx]?.dot || 'bg-gray-500'} text-white`}>
                        {stage.stage}
                      </span>
                      <span className={`text-sm font-semibold ${STAGE_COLORS[idx]?.text || 'text-gray-700'}`}>
                        Stufe {stage.stage}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-gray-400">Verzögerung (Tage nach Registrierung)</Label>
                        <Input
                          data-testid={`winback-stage-${stage.stage}-delay`}
                          type="number"
                          min={0}
                          value={stage.delay_days}
                          onChange={(e) => updateStage(idx, 'delay_days', parseInt(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-400">E-Mail Verzögerung (Std. nach WhatsApp)</Label>
                        <Input
                          data-testid={`winback-stage-${stage.stage}-email-delay`}
                          type="number"
                          min={0}
                          value={stage.email_delay_hours}
                          onChange={(e) => updateStage(idx, 'email_delay_hours', parseInt(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 mt-1">
                          <Switch
                            data-testid={`winback-stage-${stage.stage}-discount-toggle`}
                            checked={!!stage.generate_discount}
                            onCheckedChange={(checked) => updateStage(idx, 'generate_discount', checked)}
                          />
                          <Label className="text-xs text-gray-400">Rabattcode senden</Label>
                        </div>
                        {stage.generate_discount && (
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <Label className="text-xs text-gray-400">Rabatt %</Label>
                              <Input
                                data-testid={`winback-stage-${stage.stage}-discount-pct`}
                                type="number"
                                min={1}
                                max={50}
                                value={stage.discount_percent || 5}
                                onChange={(e) => updateStage(idx, 'discount_percent', parseInt(e.target.value) || 5)}
                              />
                            </div>
                            <div className="flex-1">
                              <Label className="text-xs text-gray-400">Gültig (Tage)</Label>
                              <Input
                                data-testid={`winback-stage-${stage.stage}-discount-days`}
                                type="number"
                                min={1}
                                max={90}
                                value={stage.discount_valid_days || 7}
                                onChange={(e) => updateStage(idx, 'discount_valid_days', parseInt(e.target.value) || 7)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                data-testid="winback-save-config"
                onClick={handleSaveConfig}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Speichern...' : 'Konfiguration speichern'}
              </Button>
            </div>
          )}

          {/* Vorlagen Tab */}
          {tab === 'vorlagen' && templates && (
            <div className="space-y-6" data-testid="winback-templates-tab">
              <p className="text-xs text-gray-400">
                Platzhalter: {'{{customer_name}}'}, {'{{studio_name}}'}, {'{{portal_url}}'}, {'{{discount_code}}'}, {'{{discount_percent}}'}, {'{{discount_valid_until}}'}
              </p>

              {/* Email Templates */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> E-Mail Vorlagen
                </h3>

                <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/5 space-y-3">
                  <p className="text-xs font-semibold text-blue-600">Ohne Rabatt (Stufe 1 & 2)</p>
                  <div>
                    <Label className="text-xs text-gray-400">Betreff</Label>
                    <Input
                      data-testid="winback-tpl-email-subject-no-discount"
                      value={templates.email_subject_no_discount || ''}
                      onChange={(e) => updateTemplate('email_subject_no_discount', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Nachricht</Label>
                    <textarea
                      data-testid="winback-tpl-email-body-no-discount"
                      value={templates.email_body_no_discount || ''}
                      onChange={(e) => updateTemplate('email_body_no_discount', e.target.value)}
                      rows={6}
                      className="w-full mt-1 p-2 rounded-md border bg-transparent text-sm resize-y"
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 space-y-3">
                  <p className="text-xs font-semibold text-emerald-600">Mit Rabattcode (Stufe 3 — Tag 15)</p>
                  <div>
                    <Label className="text-xs text-gray-400">Betreff</Label>
                    <Input
                      data-testid="winback-tpl-email-subject-with-discount"
                      value={templates.email_subject_with_discount || ''}
                      onChange={(e) => updateTemplate('email_subject_with_discount', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Nachricht</Label>
                    <textarea
                      data-testid="winback-tpl-email-body-with-discount"
                      value={templates.email_body_with_discount || ''}
                      onChange={(e) => updateTemplate('email_body_with_discount', e.target.value)}
                      rows={8}
                      className="w-full mt-1 p-2 rounded-md border bg-transparent text-sm resize-y"
                    />
                  </div>
                </div>
              </div>

              {/* WhatsApp Templates */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> WhatsApp Vorlagen
                </h3>

                <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/5 space-y-3">
                  <p className="text-xs font-semibold text-blue-600">Ohne Rabatt (Stufe 1 & 2)</p>
                  <div>
                    <Label className="text-xs text-gray-400">Nachricht</Label>
                    <textarea
                      data-testid="winback-tpl-wa-body-no-discount"
                      value={templates.whatsapp_body_no_discount || ''}
                      onChange={(e) => updateTemplate('whatsapp_body_no_discount', e.target.value)}
                      rows={5}
                      className="w-full mt-1 p-2 rounded-md border bg-transparent text-sm resize-y"
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 space-y-3">
                  <p className="text-xs font-semibold text-emerald-600">Mit Rabattcode (Stufe 3 — Tag 15)</p>
                  <div>
                    <Label className="text-xs text-gray-400">Nachricht</Label>
                    <textarea
                      data-testid="winback-tpl-wa-body-with-discount"
                      value={templates.whatsapp_body_with_discount || ''}
                      onChange={(e) => updateTemplate('whatsapp_body_with_discount', e.target.value)}
                      rows={6}
                      className="w-full mt-1 p-2 rounded-md border bg-transparent text-sm resize-y"
                    />
                  </div>
                </div>
              </div>

              <Button
                data-testid="winback-save-templates"
                onClick={handleSaveTemplates}
                disabled={savingTemplates}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingTemplates ? 'Speichern...' : 'Vorlagen speichern'}
              </Button>
            </div>
          )}

          {/* Customers Tab */}
          {tab === 'customers' && (
            <div className="space-y-3" data-testid="winback-customers-tab">
              {customers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  Noch keine Kunden im Win-Back Flow.
                </p>
              ) : (
                <div className="space-y-2">
                  {customers.map((c) => {
                    const statusInfo = STATUS_MAP[c.status] || STATUS_MAP.active;
                    return (
                      <div
                        key={c.id}
                        data-testid={`winback-customer-${c.id}`}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg border bg-card gap-2"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">
                              {c.customer_name || 'Unbekannt'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {c.customer_email || c.customer_phone || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            Stufe {c.current_stage || 1}
                          </Badge>
                          <Badge className={`text-xs border ${statusInfo.color}`}>
                            {statusInfo.label}
                          </Badge>
                          {c.discount_code && (
                            <Badge
                              data-testid={`winback-code-${c.id}`}
                              className="text-xs bg-amber-500/15 text-amber-600 border-amber-500/30 font-mono"
                              title={`Rabattcode ${c.discount_code} (${c.discount_percent || '?'}%)`}
                            >
                              🎁 {c.discount_code}
                            </Badge>
                          )}
                          <span className="text-xs text-gray-500">
                            {formatDate(c.created_at)}
                          </span>
                          {c.status === 'active' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`winback-remove-${c.id}`}
                              onClick={() => handleRemoveCustomer(c.id)}
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-300"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Activity Tab */}
          {tab === 'activity' && (
            <div className="space-y-2" data-testid="winback-activity-tab">
              {activity.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  Noch keine Aktivitäten.
                </p>
              ) : (
                activity.map((a, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-2 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <ActivityIcon action={a.action} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{a.message}</p>
                      <span className="text-xs text-gray-500">{formatDate(a.timestamp)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

/* ─── Sub-Components ─────────────────────────────────────── */

const StatCard = ({ label, value, icon: Icon, color = 'text-gray-700' }) => (
  <div className="p-3 rounded-lg border bg-card text-center" data-testid={`winback-stat-${label.toLowerCase()}`}>
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <div className={`text-lg font-bold ${color}`}>{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

const ActivityIcon = ({ action }) => {
  const map = {
    entered_flow: <Users className="w-4 h-4 text-blue-600 mt-0.5" />,
    whatsapp_sent: <MessageSquare className="w-4 h-4 text-green-600 mt-0.5" />,
    email_sent: <Mail className="w-4 h-4 text-amber-600 mt-0.5" />,
    converted: <UserCheck className="w-4 h-4 text-emerald-600 mt-0.5" />,
    stage_advanced: <ArrowRight className="w-4 h-4 text-purple-400 mt-0.5" />,
    flow_completed: <BarChart3 className="w-4 h-4 text-gray-400 mt-0.5" />,
  };
  return map[action] || <Activity className="w-4 h-4 text-gray-400 mt-0.5" />;
};

const FlowVisualization = ({ config, stats }) => {
  if (!config?.stages) return null;

  return (
    <div className="space-y-4" data-testid="winback-flow-viz">
      {/* Visual Pipeline */}
      <div className="relative">
        {/* Start Node */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-100 border border-green-300 flex items-center justify-center">
            <Users className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-green-600">Neukunde registriert</p>
            <p className="text-xs text-gray-500">Keine Buchung innerhalb der Verzögerungszeit</p>
          </div>
        </div>

        {/* Connector */}
        <div className="ml-5 h-6 border-l-2 border-dashed border-gray-300" />

        {/* Stages */}
        {config.stages.map((stage, idx) => {
          const colors = STAGE_COLORS[idx] || STAGE_COLORS[0];
          const stageCount = stats?.stages?.[`stage_${stage.stage}`] || 0;

          return (
            <div key={idx}>
              <div
                data-testid={`winback-flow-stage-${stage.stage}`}
                className={`ml-0 p-4 rounded-lg border ${colors.border} ${colors.bg} relative`}
              >
                {/* Stage Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${colors.dot} text-white`}>
                      {stage.stage}
                    </span>
                    <span className={`text-sm font-semibold ${colors.text}`}>
                      Stufe {stage.stage} - Tag {stage.delay_days}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {stageCount} Kunden
                  </Badge>
                </div>

                {/* Stage Actions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 p-2 rounded-md bg-white border border-gray-200">
                    <MessageSquare className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-green-600">WhatsApp</p>
                      <p className="text-xs text-gray-500">Sofort bei Stufenstart</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-white border border-gray-200">
                    <Mail className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-amber-600">E-Mail</p>
                      <p className="text-xs text-gray-500">+{stage.email_delay_hours}h nach WhatsApp</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Connector between stages */}
              {idx < config.stages.length - 1 && (
                <div className="flex items-center ml-5 my-1">
                  <div className="h-8 border-l-2 border-dashed border-gray-300" />
                </div>
              )}
            </div>
          );
        })}

        {/* Connector to end */}
        <div className="ml-5 h-6 border-l-2 border-dashed border-gray-300" />

        {/* End Nodes */}
        <div className="flex flex-wrap gap-3 mt-2">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-300">
            <UserCheck className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-medium text-emerald-600">
              Konvertiert ({stats?.converted || 0})
            </span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-orange-50 border border-orange-300">
            <UserX className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-medium text-orange-600">
              Abgemeldet ({stats?.unsubscribed || 0})
            </span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-100 border border-gray-300">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-400">
              Abgeschlossen ({stats?.completed || 0})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
